import React, { FC } from 'react';
import { Icon } from 'src/components/widgets/icon';
import { ZTooltip } from 'src/components/widgets/ztooltip';
import MicrophoneIcon from 'src/shared/material_icons/legacy/microphone-with-gradient.svg';
import { T } from 'src/shared/helpers';
import styled, { keyframes } from 'styled-components';

const bounce = keyframes`
    0%, 100% {
        transform: scaleY(1);
    }
    50% {
        transform: scaleY(1.45);
    }
`;

const Button = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: white;
    color: #6b7280;
    cursor: pointer;
    transition: all 0.2s ease;
    flex-shrink: 0;

    &:hover:not(:disabled) {
        background: #eaeaea;
        color: #374151;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    ${Icon} {
        width: 24px;
        height: 24px;
        min-width: 0;

        svg {
            width: 24px;
            height: 24px;
        }
    }
`;

const ListeningDots = styled.svg`
    .dot-1 {
        animation: ${bounce} 0.75s ease-in-out infinite;
        transform-origin: center;
    }

    .dot-2 {
        animation: ${bounce} 0.75s ease-in-out 0.15s infinite;
        transform-origin: center;
    }

    .dot-3 {
        animation: ${bounce} 0.75s ease-in-out 0.3s infinite;
        transform-origin: center;
    }
`;

const AnimatedListeningDots: FC = () => (
    <ListeningDots xmlns='http://www.w3.org/2000/svg' viewBox='0 -960 960 960' aria-hidden='true'>
        <defs>
            <linearGradient id='agent-listening-dots-gradient' x1='160' y1='-480' x2='800' y2='-480' gradientUnits='userSpaceOnUse'>
                <stop stopColor='#3b82f6' />
                <stop offset='1' stopColor='#8b5cf6' />
            </linearGradient>
        </defs>
        <circle className='dot-1' cx='240' cy='-320' r='80' fill='url(#agent-listening-dots-gradient)' />
        <circle className='dot-2' cx='480' cy='-320' r='80' fill='url(#agent-listening-dots-gradient)' />
        <circle className='dot-3' cx='720' cy='-320' r='80' fill='url(#agent-listening-dots-gradient)' />
    </ListeningDots>
);

interface VoiceInputButtonProps {
    disabled?: boolean;
    isListening: boolean;
    onClick: () => void;
}

export const VoiceInputButton: FC<VoiceInputButtonProps> = ({ disabled = false, isListening, onClick }) => {
    const label = isListening ? T._('Stop recording', 'Admin') : T._('Start voice input', 'Admin');

    return (
        <ZTooltip html={label} position='top'>
            <Button type='button' disabled={disabled} aria-label={label} aria-pressed={isListening} onClick={onClick}>
                <Icon>{isListening ? <AnimatedListeningDots /> : <MicrophoneIcon />}</Icon>
            </Button>
        </ZTooltip>
    );
};
